import { useState, useRef, useEffect } from 'react';
import { outboundAPI } from '../api/client';
import { useTranslation } from '../hooks/useTranslation';
import { TranslationPill } from './TranslationPill';
import { VoiceRecorder } from './VoiceRecorder';

interface MessageComposerProps {
  contactId: number;
  userLanguage: string;
  contactLanguage: string | null;
  onSent?: () => void;
}

export function MessageComposer({ contactId, userLanguage, contactLanguage, onSent }: MessageComposerProps) {
  const [content, setContent] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [content]);

  const {
    hasLanguageMismatch, translateEnabled, setTranslateEnabled,
    preview, loading: translating, getTranslationPreview, clearPreview, targetLanguage,
  } = useTranslation({
    userLanguage,
    contactLanguage,
  });

  const handleVoiceTranscribed = (text: string) => {
    setContent(prev => prev ? `${prev} ${text}` : text);
  };

  async function handleSend() {
    const text = content.trim();
    if (!text || sending) return;

    try {
      setSending(true);
      setError(null);

      let finalContent = text;
      if (translateEnabled && hasLanguageMismatch) {
        const translated = await getTranslationPreview(text);
        if (translated) finalContent = translated;
      }

      const result = await outboundAPI.sendNow(contactId, finalContent);
      if (result.success) {
        setContent('');
        clearPreview();
        onSent?.();
      } else {
        setError(result.error || 'Failed to send');
      }
    } catch (err) {
      console.error('Send failed:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="border-t border-edge bg-white px-4 py-3 space-y-2">
      {/* Translation controls */}
      {hasLanguageMismatch && (
        <div className="flex items-center gap-2">
          <TranslationPill
            targetLanguage={targetLanguage}
            enabled={translateEnabled}
            onToggle={setTranslateEnabled}
          />
          {translateEnabled && content.trim() && (
            <button
              type="button"
              onClick={() => getTranslationPreview(content)}
              className="text-xs text-accent/60 hover:text-accent"
              disabled={translating}
            >
              {translating ? 'Translating...' : 'Preview'}
            </button>
          )}
        </div>
      )}

      {/* Translation preview */}
      {preview && (
        <div className="rounded-lg bg-accent/5 border border-accent/10 px-3 py-2 text-sm text-accent/80">
          {preview}
        </div>
      )}

      {/* Input row */}
      <div className="flex items-end gap-2">
        <textarea
          ref={textareaRef}
          value={content}
          onChange={(e) => { setContent(e.target.value); clearPreview(); }}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder="Type a message..."
          className="flex-1 rounded-lg border border-edge px-3 py-2 text-sm resize-none focus:outline-none focus:border-accent/40 overflow-y-auto"
          style={{ minHeight: '38px', maxHeight: '120px' }}
        />
        <VoiceRecorder onTranscribed={handleVoiceTranscribed} />
        <button
          onClick={handleSend}
          disabled={sending || !content.trim()}
          className="rounded-full bg-accent text-white p-2 disabled:opacity-40 hover:bg-accent/90 transition-colors"
          title="Send"
        >
          <span className="material-icons-round text-lg leading-none">send</span>
        </button>
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
